'use client';

import { Fullstory } from '@fullstory/browser';

import { type ReactNode, useEffect, useMemo } from 'react';

import { filterBlank } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { isEmptyArray } from '@dte/otw-utils-core/src/objects/isEmpty';

import { isProd } from '../../common/util/environmentConfig';
import { useMsalAccount } from '../../contexts/TokenContext/useMsalAccount';
import { useRoles } from '../../contexts/TokenContext/useRoles';
import type { FullstoryIdentity, FullstoryUserProperties } from './FullStoryIdentity';
import { useFullstoryReady } from './useFullStoryReady';

function useFullstoryIdentity(): FullstoryIdentity | undefined {
	// Get the account from MSAL
	const account = useMsalAccount();

	// Keep track of the parts of the account info that we need to send to Fullstory
	const identity = useMemo(() => {
		const uid = account?.homeAccountId;
		if (!uid) {
			return undefined;
		}

		const email = account.username;
		if (!email) {
			return undefined;
		}
		const displayName = account.name;

		const newIdentity: FullstoryIdentity = {
			anonymous: false,
			uid: uid,
			properties: {
				displayName: displayName,
				email: email,
			},
		};
		return newIdentity;
	}, [account]);

	return identity;
}

function useLogUserRoles(): void {
	const [fullStoryReady] = useFullstoryReady();

	const roles = useRoles();

	// Stable / sorted roles list
	const rolesList = useMemo(() => {
		const newRoles = filterBlank(roles);
		if (isEmptyArray(newRoles)) {
			return undefined;
		}
		newRoles.sort();
		return newRoles.join(',');
	}, [roles]);

	const roleProperties = useMemo(() => {
		if (!rolesList) {
			return undefined;
		}

		const properties: FullstoryUserProperties = {
			type: 'user',
			properties: {
				roles: rolesList,
			},
		};

		return properties;
	}, [rolesList]);

	// If the parts we care about change, send that fo Fullstory
	useEffect(() => {
		// No data
		if (!roleProperties) {
			return;
		}
		if (!fullStoryReady) {
			return;
		}

		if (!isProd) {
			console.log('Logging user roles to Fullstory: ', roleProperties.properties.roles);
		}

		// Log user properties to Fullstory
		Fullstory('setProperties', roleProperties);
	}, [roleProperties, fullStoryReady]);
}

function useLogUserIdentity(): void {
	// Keep track of the parts of the account info that we need to send to Fullstory
	const identity = useFullstoryIdentity();
	const [fullStoryReady] = useFullstoryReady();

	// If the parts we care about change, send that fo Fullstory
	useEffect(() => {
		// No data
		if (!identity) {
			return;
		}
		if (!fullStoryReady) {
			return;
		}

		if (!isProd) {
			console.log('Logging user to Fullstory: ', identity.properties?.email);
		}

		// Log user information to Fullstory
		Fullstory('setIdentity', identity);
	}, [identity, fullStoryReady]);
}

export function FullstoryUserLogger(): ReactNode {
	useLogUserIdentity();
	useLogUserRoles();

	return null;
}
